import './styles.css';
import './form-tweaks.css';
import { blockedAfterReturn, dayFlowState, dayFormType, lastReturnReadings, bangkokDayKey, groupEventsByDay, normalizeEvent, searchHistoryEvents, topLocations, type SearchField, type EventInput, type EventType } from '@ev-trip/domain';
import { registerSW } from 'virtual:pwa-register';
import { bootstrap, enqueue, flushQueue, getSettings, history, latestEvent, latestBookend, retryMutation, signIn, signOut, supabase, updateSettings, type UserSettings } from './api';
import { recoverSession } from './auth-session';
import { chargeDashboardHTML } from './charge-dashboard';
import { clearDraft, loadDraft, queue, saveDraft, type PendingMutation } from './db';
import { configured, env } from './env';
import { saveFeedback } from './save-feedback';
import { APP_COMMIT } from './app-commit.generated';

registerSW({ immediate: true });

type ApiEvent = Record<string, unknown> & {
  event_id: string; version: number; type: EventType; occurred_at: string; location_name_snapshot: string;
  job_type_snapshot?: string; odometer: number; battery_pct?: number | null; battery_before_pct?: number | null;
  battery_after_pct?: number | null; charge_kwh?: number | null; cost?: number | null; notes?: string;
};
type Boot = {
  revision: string | null; today: string; summary: { distance_km?: number; cost?: number; charge_kwh?: number; trips?: number };
  recent: ApiEvent[]; locations: { location_id: string; name: string; kind: string }[];
  workTypes: { work_type_id: string; name: string }[]; vehicle?: { vehicle_id: string; name: string; battery_capacity_kwh: number };
};

const root = document.querySelector<HTMLElement>('#app')!;
// Which build is running and which Supabase project it talks to (commit · dev/build · backend).
const versionLabel = () => {
  const mode = import.meta.env.DEV ? 'dev' : 'build';
  const backend = env.url.match(/^https:\/\/([a-z0-9]+)/)?.[1] ?? '—';
  return `เวอร์ชัน ${APP_COMMIT} · ${mode} · ฐานข้อมูล ${backend.slice(0, 8)}`;
};
let state: Boot | null = null;
// GPS one-shot suggestion: only queried when entering the trip tab, cached for 10 minutes.
let gpsLastLookup = 0;
let gpsInFlight = false;
let lastGpsCoords: { lat: number; lon: number } | null = null;
const GPS_CACHE_MS = 10 * 60 * 1000;

// Per-device learned coordinates: location name -> GPS where the user saved it.
// Stored in localStorage so no DB migration is needed; private to this device.
function getLearnedCoords(name: string): { lat: number; lon: number } | null {
  try {
    const all = JSON.parse(localStorage.getItem('locationCoords') || '{}');
    const p = all[name.toLowerCase().trim()];
    return p && isFinite(p.lat) && isFinite(p.lon) ? p : null;
  } catch { return null; }
}

function learnCoords(name: string, coords: { lat: number; lon: number }) {
  try {
    const all = JSON.parse(localStorage.getItem('locationCoords') || '{}');
    all[name.toLowerCase().trim()] = coords;
    localStorage.setItem('locationCoords', JSON.stringify(all));
  } catch {}
}

async function reverseGeocode(lat: number, lon: number): Promise<string | null> {
  try {
    const url = `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json&accept-language=th&zoom=18`;
    const res = await fetch(url, { headers: { 'Accept': 'application/json' } });
    if (!res.ok) return null;
    const data = await res.json();
    const addr = data?.address ?? {};
    // Build a concise Thai address: road + suburb + district + province.
    const parts = [addr.road, addr.suburb ?? addr.neighbourhood, addr.city_district ?? addr.district, addr.city ?? addr.town ?? addr.village, addr.state].filter(Boolean);
    if (parts.length) return parts.join(' ');
    return data?.display_name?.split(',').slice(0, 3).join(',').trim() || null;
  } catch { return null; }
}

function haversineM(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371000;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function getCachedCoords(name: string): { lat: number; lon: number } | null {
  try {
    const raw = localStorage.getItem(`geocode:${name.toLowerCase().trim()}`);
    if (!raw) return null;
    const p = JSON.parse(raw);
    return isFinite(p.lat) && isFinite(p.lon) ? p : null;
  } catch { return null; }
}

function setCachedCoords(name: string, lat: number, lon: number) {
  try { localStorage.setItem(`geocode:${name.toLowerCase().trim()}`, JSON.stringify({ lat, lon })); } catch {}
}

async function geocodePlace(name: string): Promise<{ lat: number; lon: number } | null> {
  const cached = getCachedCoords(name);
  if (cached) return cached;
  try {
    const res = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(name)}&format=json&limit=1&accept-language=th`, { headers: { 'Accept': 'application/json' } });
    if (!res.ok) return null;
    const data = await res.json();
    if (data?.[0]) {
      const lat = parseFloat(data[0].lat), lon = parseFloat(data[0].lon);
      if (isFinite(lat) && isFinite(lon)) { setCachedCoords(name, lat, lon); return { lat, lon }; }
    }
  } catch {}
  return null;
}

// Find a saved location within 300m of the GPS position.
// Priority: 1) device-learned coords (user saved here before), 2) cached geocoding.
async function findNearbySavedLocation(lat: number, lon: number): Promise<{ name: string; dist: number } | null> {
  const seen = new Set<string>();
  const candidates: string[] = [];
  // Prefer recently visited customer locations.
  for (const e of (state?.recent ?? [])) {
    const n = (e as { location_name_snapshot?: string }).location_name_snapshot;
    if (n && !seen.has(n)) { seen.add(n); candidates.push(n); if (candidates.length >= 8) break; }
  }
  // Fall back to all customer locations if recent is empty.
  if (!candidates.length) {
    for (const l of (state?.locations ?? [])) {
      if (l.kind === 'charge' || seen.has(l.name)) continue;
      seen.add(l.name); candidates.push(l.name);
      if (candidates.length >= 8) break;
    }
  }
  let best: { name: string; dist: number } | null = null;
  for (const name of candidates) {
    // 1) Use device-learned coordinates if available (no API call).
    let c = getLearnedCoords(name);
    let geocoded = false;
    if (!c) { c = await geocodePlace(name); geocoded = true; }
    // Respect Nominatim's 1 req/sec policy on uncached lookups.
    if (geocoded && !getCachedCoords(name)) await new Promise(r => setTimeout(r, 1100));
    if (!c) continue;
    const dist = haversineM(lat, lon, c.lat, c.lon);
    if (dist <= 300 && (!best || dist < best.dist)) best = { name, dist };
    if (best && best.dist < 50) break; // Very close — good enough.
  }
  return best;
}

function showGpsSuggestion(form: HTMLFormElement, label: string, name: string) {
  if (!form.isConnected) return;
  const box = form.querySelector<HTMLElement>('.gps-suggest');
  const btn = form.querySelector<HTMLButtonElement>('.gps-pick');
  const hint = form.querySelector<HTMLElement>('.gps-hint');
  if (hint) hint.hidden = true;
  if (name && box && btn) {
    btn.textContent = `${label} ${name}`;
    btn.onclick = () => {
      const input = form.elements.namedItem('locationName') as HTMLInputElement;
      input.value = name;
      input.dispatchEvent(new Event('input', { bubbles: true }));
      box.hidden = true;
    };
    box.hidden = false;
  }
}

function requestGpsSuggestion(form: HTMLFormElement) {
  if (gpsInFlight || !navigator.geolocation) return;
  const now = Date.now();
  if (now - gpsLastLookup < GPS_CACHE_MS) return;
  // Only for new-entry forms, not the edit dialog.
  if (form.dataset.eventId) return;
  gpsInFlight = true;
  gpsLastLookup = now;
  const hint = form.querySelector<HTMLElement>('.gps-hint');
  if (hint) { hint.hidden = false; hint.textContent = '📍 กำลังหาตำแหน่ง...'; }
  navigator.geolocation.getCurrentPosition(
    async pos => {
      gpsInFlight = false;
      const { latitude: lat, longitude: lon } = pos.coords;
      lastGpsCoords = { lat, lon };
      // 1) Check saved locations within 300m first — most likely the right name.
      const nearby = await findNearbySavedLocation(lat, lon);
      if (!form.isConnected) return;
      if (nearby) {
        showGpsSuggestion(form, `📍 ใกล้คุณ (${Math.round(nearby.dist)} ม.)`, nearby.name);
        return;
      }
      // 2) Fall back to reverse-geocoded address.
      const name = await reverseGeocode(lat, lon);
      if (name) showGpsSuggestion(form, '📍', name);
      else { const hint = form.querySelector<HTMLElement>('.gps-hint'); if (hint) hint.hidden = true; }
    },
    () => {
      gpsInFlight = false;
      if (hint) hint.hidden = true;
      // Permission denied or unavailable — silently skip, user can type manually.
    },
    { enableHighAccuracy: false, timeout: 10000, maximumAge: 600000 }
  );
}
let knownBookends: ApiEvent[] = [];
let settings: UserSettings | null = null;
let tab: 'day' | 'trip' | 'charge' = 'trip';
let tabInit = false;
let pendingBookends: PendingMutation[] = [];
let allEvents: ApiEvent[] = [];
let allEventsCursor: string | null = null;
let allEventsComplete = false;
let eventsLoad: Promise<void> | null = null;
let historyQuery = '';
let historyMode: 'matches' | 'day' = 'matches';
let historyBase = '';
let historyLocal = false;
let stats: 'loading' | 'error' | 'ready' = 'loading';
// The menu's charge-dashboard dialog remembers the month the owner picked
// ('YYYY-MM' | 'all', null = all-time default); the charge tab keeps the
// original three-period summary and is untouched by it.
let chargeMonth: string | 'all' | null = null;
const typeLabels: Record<EventType, string> = { start: 'ออกเดินทาง', trip: 'ถึงจุดหมาย', charge: 'ชาร์จไฟ', end: 'กลับบ้าน' };
const escape = (value: unknown) => String(value ?? '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);
const localValue = (iso?: string) => {
  const date = iso ? new Date(iso) : new Date();
  return new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
};
const thaiDate = (iso: unknown) => new Date(String(iso)).toLocaleString('th-TH', { timeZone: 'Asia/Bangkok', dateStyle: 'medium', timeStyle: 'short' });
const thaiTime = (iso: string) => new Date(iso).toLocaleTimeString('th-TH', { timeZone: 'Asia/Bangkok', hour: '2-digit', minute: '2-digit' });
const icon = (name: 'sync'|'menu'|'home'|'pin'|'charge'|'car'|'clock'|'note'|'battery'|'close') => {
  const paths = {
    sync:'<path d="M20 8a8 8 0 0 0-14-3L3 8m0-5v5h5M4 16a8 8 0 0 0 14 3l3-3m0 5v-5h-5"/>', menu:'<path d="M3 5h18M3 12h18M3 19h18"/>',
    home:'<path d="m3 11 9-8 9 8M5 10v11h5v-7h4v7h5V10"/>', pin:'<path d="M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 1 1 16 0Z"/><circle cx="12" cy="10" r="2.5"/>',
    charge:'<path d="M8 2v6m8-6v6M6 8h12v5a6 6 0 0 1-12 0V8Zm6 11v4"/>', car:'<path d="m4 10 2-6h12l2 6M3 17V11l2-1h14l2 1v6H3Zm1 0v3m16-3v3M6 13h2m8 0h2"/>',
    clock:'<circle cx="12" cy="12" r="9"/><path d="M12 6v6l4 2"/>', note:'<path d="M5 3h10l4 4v14H5V3Zm10 0v5h4M9 12h6m-6 4h6"/>', battery:'<rect x="6" y="4" width="12" height="18" rx="2"/><path d="M10 1h4v3M9 10v9h6v-9"/>', close:'<path d="m6 6 12 12M6 18 18 6"/>'
  };
  return `<svg aria-hidden="true" viewBox="0 0 24 24">${paths[name]}</svg>`;
};

function shell(body: string) {
  root.innerHTML = `<div class="shell"><header class="topbar"><img class="brand-img" src="/worktripev-brand.png" alt="WorkTripEV" width="362" height="232"><div class="top-actions"><button id="retry" class="sync-button">${icon('sync')}<span>ซิงก์</span></button><button id="menu-toggle" class="icon-button" aria-label="เปิดเมนู" aria-expanded="false">${icon('menu')}</button></div></header><div id="app-menu" class="app-menu" hidden><button id="charge-dashboard">แดชบอร์ดค่าชาร์จ</button><button id="history">ประวัติทั้งหมด</button><button id="settings">ตั้งค่าการเชื่อมต่อ</button><button id="logout">ออกจากระบบ</button><small class="menu-version">${versionLabel()}</small></div><main><div id="network" class="network" role="status"></div>${body}</main><dialog id="dialog"><button id="dialog-close" class="dialog-close" aria-label="ปิด">${icon('close')}</button><div id="dialog-body"></div></dialog><nav><button data-tab="day">${icon('home')}<span>เริ่ม/จบวัน</span></button><button data-tab="trip" class="active">${icon('pin')}<span>ถึงจุดหมาย</span></button><button data-tab="charge">${icon('charge')}<span>ชาร์จ</span></button></nav></div>`;
  document.querySelector('#logout')?.addEventListener('click', () => signOut());
  document.querySelector('#charge-dashboard')?.addEventListener('click', () => void openChargeDashboard());
  document.querySelector('#history')?.addEventListener('click', () => openHistory());
  document.querySelector('#settings')?.addEventListener('click', () => openSettings());
  document.querySelector('#dialog-close')?.addEventListener('click', closeDialog);
  document.querySelector('#menu-toggle')?.addEventListener('click', () => { const menu = document.querySelector<HTMLElement>('#app-menu')!; menu.hidden = !menu.hidden; document.querySelector('#menu-toggle')?.setAttribute('aria-expanded', String(!menu.hidden)); });
  // Every menu entry opens a dialog — close the dropdown so it never sits behind it.
  document.querySelector('#app-menu')?.addEventListener('click', () => { const menu = document.querySelector<HTMLElement>('#app-menu')!; menu.hidden = true; document.querySelector('#menu-toggle')?.setAttribute('aria-expanded', 'false'); });
}

function eventCard(event: ApiEvent) {
  const eventIcon = event.type === 'charge' ? 'charge' : event.type === 'trip' ? 'pin' : event.type === 'start' ? 'car' : 'home';
  return `<button class="event-row" data-event-id="${escape(event.event_id)}"><span class="event-icon ${event.type}">${icon(eventIcon)}</span><span class="event-copy"><strong>${typeLabels[event.type] ?? escape(event.type)}</strong><small>${escape(event.location_name_snapshot)} · ${thaiDate(event.occurred_at)}</small></span><span class="chevron">›</span></button>`;
}

function pendingCard(item: PendingMutation) {
  const label = item.state === 'conflict' ? 'ต้องตรวจสอบข้อมูลชนกัน' : item.state === 'failed' ? 'ซิงก์ไม่สำเร็จ' : item.state === 'syncing' ? 'กำลังซิงก์' : 'รอซิงก์';
  return `<article class="pending-row ${item.state}"><div><strong>${escape(typeLabels[item.event.type])}</strong><span>${escape(item.event.locationName)}</span><small>${escape(label)}${item.error ? ` · ${escape(item.error)}` : ''}</small></div>${item.state === 'conflict' ? `<button class="ghost resolve-conflict" data-id="${item.id}">ตรวจสอบ</button>` : item.state === 'failed' ? `<button class="ghost retry-one" data-id="${item.id}">ลองใหม่</button>` : ''}</article>`;
}

async function render() {
  if (!tabInit) { tab = dayState().latest === 'start' ? 'trip' : 'day'; tabInit = true; }
  const pending = await queue.all();
  pendingBookends = pending;
  if (dayState().closed) tab = 'day';
  const summary = state?.summary ?? {};
  shell(`<section class="heading"><div><h1>สรุปวันนี้</h1><p class="subtitle">${escape(state?.today ?? '')}</p></div><p class="safe-copy">ขับขี่ปลอดภัย<br>ทุกเส้นทางครับ</p></section><section class="summary-card"><div class="summary-title"><span>${icon('car')} ระยะทางวันนี้</span>${icon('battery')}</div><strong class="distance">${summary.distance_km ?? 0} <small>km</small></strong><div class="summary-grid"><div><strong>${summary.charge_kwh ?? 0} <small>kWh</small></strong><span>พลังงานชาร์จ</span></div><div><strong>${summary.cost ?? 0} <small>฿</small></strong><span>ค่าใช้จ่าย</span></div><div><strong>${summary.trips ?? 0}</strong><span>จุดหมาย</span></div></div></section>
    ${pending.length ? `<section class="panel pending-panel"><div class="section-title"><h2>สถานะการซิงก์</h2><button id="retry-panel" class="link-button">ซิงก์ใหม่</button></div>${pending.map(pendingCard).join('')}</section>` : ''}
    <section class="panel form-card"><div class="section-title"><div><small>รายการใหม่</small><h2 id="form-title">ถึงจุดหมาย</h2></div><span class="badge">${navigator.onLine ? 'ออนไลน์' : 'ออฟไลน์'}</span></div>${eventForm()}</section>
    <div id="sections-host">${sections()}</div>`);
  bindMain();
  updateNetwork();
}

function eventForm(event?: ApiEvent) {
  const type = event?.type ?? (tab === 'day' ? dayFormType(dayState()) : tab);
  const day = type === 'start' || type === 'end';
  const pre = type === 'start' ? startPrefill() : { odo: undefined as number | undefined, bat: undefined as number | undefined };
  const prefill = (current: number | null | undefined, fallback: number | string | undefined) => escape(event ? current ?? '' : fallback ?? '');
  const customerSpots = (state?.locations ?? []).filter(l => l.kind !== 'charge');
  const chargeSpots = (state?.locations ?? []).filter(l => l.kind === 'charge');
  return `<form id="event-form" data-event-id="${escape(event?.event_id ?? '')}" data-version="${escape(event?.version ?? '')}">${event ? '' : '<div class="segmented day-seg" hidden><button type="button" data-day="start">🚗 ออกเดินทาง</button><button type="button" data-day="end">🏠 กลับบ้าน</button></div><small class="day-hint" hidden></small>'}<input type="hidden" name="type" value="${type}"><div class="meta-row"><button type="button" class="chip time-chip">🕒 <span>${localValue(event?.occurred_at).replace('T', ' ')}</span></button><input class="time-input" name="occurredAt" type="datetime-local" value="${localValue(event?.occurred_at)}" required hidden><span class="chip loc-badge" hidden>🏠 ที่พัก</span>${event ? '' : latestHint()}</div>${!event && blockedAfterReturn(type, dayState().closed) ? '<small class="trip-hint">กลับบ้านแล้ว กรุณาออกเดินทางใหม่ก่อนบันทึกจุดหมายหรือชาร์จ</small>' : ''}<label class="loc-field"><span class="field-label">${icon('pin')}สถานที่</span><input name="locationName" list="locations" maxlength="150" autocomplete="off" value="${escape(event?.location_name_snapshot ?? '')}" required placeholder="เลือกหรือพิมพ์สถานที่"></label><div class="gps-suggest" hidden><button type="button" class="gps-pick"></button></div><small class="gps-hint" hidden></small><div class="charge-shortcuts" role="group" aria-label="เลือกสถานที่ชาร์จด่วน" hidden>${['Spark','EVST','OneCharge'].map(name => `<button type="button" data-charge-location="${name}" aria-pressed="false">${name}</button>`).join('')}</div><datalist id="locations">${customerSpots.map(l => `<option value="${escape(l.name)}">`).join('')}</datalist><datalist id="charge-locations">${chargeSpots.map(l => `<option value="${escape(l.name)}">`).join('')}</datalist><div class="job-field"><label><span class="field-label">${icon('note')}ประเภทงาน</span><select name="jobType">${(state?.workTypes ?? []).map(j => `<option${j.name === (event?.job_type_snapshot ?? 'PM') ? ' selected' : ''}>${escape(j.name)}</option>`).join('')}</select><span class="job-new-row" hidden><input name="jobTypeNew" list="job-types" maxlength="80" autocomplete="off" placeholder="พิมพ์ชื่อประเภทงานใหม่"><button type="button" class="job-confirm" aria-label="ยืนยัน">✓</button><button type="button" class="job-cancel" aria-label="ยกเลิก">✕</button></span></label><div class="job-shortcuts" role="group" aria-label="เลือกประเภทงานด่วน"><button type="button" data-job-type="PM">PM</button><button type="button" data-job-type="Call">Call</button><button type="button" class="job-add" aria-label="เพิ่มประเภทงานใหม่">＋</button></div></div><datalist id="job-types">${(state?.workTypes ?? []).map(j => `<option value="${escape(j.name)}">`).join('')}</datalist><div class="grid"><label>เลขไมล์ (km)<input name="odometer" type="number" min="0" max="9999999" step="0.1" inputmode="decimal" value="${prefill(event?.odometer, pre.odo)}" required placeholder="เลขไมล์บนหน้าปัด"></label><label class="battery-field">แบตเตอรี่ (%)<input name="batteryPct" type="number" min="0" max="100" step="1" inputmode="decimal" value="${prefill(event?.battery_pct, pre.bat)}" required placeholder="0–100"></label></div><div class="charge-fields" hidden><div class="grid"><label>ก่อนชาร์จ (%)<input name="batteryBeforePct" type="number" min="0" max="100" inputmode="decimal" value="${escape(event?.battery_before_pct ?? '')}"></label><label>หลังชาร์จ (%)<input name="batteryAfterPct" type="number" min="0" max="100" inputmode="decimal" value="${escape(event?.battery_after_pct ?? '')}"></label><label>พลังงาน (kWh)<input name="chargeKwh" type="number" min="0.001" step="0.001" inputmode="decimal" value="${escape(event?.charge_kwh ?? '')}"></label><label>ค่าใช้จ่าย (฿)<input name="cost" type="number" min="0" step="0.01" inputmode="decimal" value="${escape(event?.cost ?? '')}"></label></div></div><label><span class="field-label">${icon('note')}หมายเหตุ</span><textarea name="notes" maxlength="1000" placeholder="รายละเอียดเพิ่มเติม (ถ้ามี)">${escape(event?.notes ?? '')}</textarea></label><p id="form-error" class="error" role="alert"></p><button class="primary" type="submit">${event ? 'บันทึกการแก้ไข' : 'บันทึกรายการ'}</button></form>`;
}

function setType(type: EventType, form = document.querySelector<HTMLFormElement>('#event-form')!) {
  const typeInput = form.querySelector<HTMLInputElement>('input[name="type"]')!;
  typeInput.value = type;
  typeInput.setAttribute('value', type);
  const day = type === 'start' || type === 'end';
  if (!form.dataset.eventId) {
    tab = day ? 'day' : (type as 'trip' | 'charge');
    document.querySelectorAll('nav button').forEach(b => b.classList.toggle('active', (b as HTMLElement).dataset.tab === tab));
    document.querySelectorAll<HTMLElement>('.tab-sec').forEach(sec => { sec.hidden = sec.dataset.tab !== tab; });
  }
  const locked = dayState();
  document.querySelectorAll<HTMLButtonElement>('nav [data-tab]').forEach(button => {
    const blocked = locked.closed && button.dataset.tab !== 'day';
    button.disabled = blocked;
    button.setAttribute('aria-disabled', String(blocked));
    button.title = blocked ? 'กลับบ้านแล้ว กรุณาออกเดินทางใหม่ก่อน' : '';
  });
  if (!form.dataset.eventId && blockedAfterReturn(type, locked.closed)) {
    setType('start', form); return;
  }
  form.querySelectorAll<HTMLButtonElement>('.day-seg button').forEach(b => {
    b.classList.toggle('active', (b as HTMLElement).dataset.day === type);
    b.disabled = !form.dataset.eventId && ((b as HTMLElement).dataset.day === 'start' ? locked.latest === 'start' : locked.closed);
  });
  const dayHint = form.querySelector<HTMLElement>('.day-hint');
  if (dayHint) {
    const closed = locked.closed; const opened = locked.latest === 'start';
    dayHint.hidden = !(opened || closed);
    dayHint.textContent = closed ? 'กลับบ้านแล้ว กด "ออกเดินทาง" เริ่มรอบใหม่ได้ (จะมีคำเตือนก่อนบันทึก)' : 'ออกเดินทางอยู่ ยังไม่กด "กลับบ้าน" วันนี้';
  }
  form.querySelector<HTMLElement>('.day-seg')?.toggleAttribute('hidden', !day);
  form.querySelector<HTMLElement>('.loc-field')!.hidden = day;
  form.querySelector<HTMLElement>('.loc-badge')!.hidden = !day;
  const title = document.querySelector('#form-title'); if (title) title.textContent = typeLabels[type];
  const charge = type === 'charge';
  form.querySelector<HTMLElement>('.charge-fields')!.hidden = !charge;
  form.querySelector<HTMLElement>('.charge-shortcuts')!.hidden = !charge;
  form.querySelector<HTMLElement>('.battery-field')!.hidden = charge;
  form.querySelector<HTMLElement>('.job-field')!.hidden = type !== 'trip';
  const location = form.elements.namedItem('locationName') as HTMLInputElement;
  location.setAttribute('list', charge ? 'charge-locations' : 'locations');
  location.disabled = type === 'start' || type === 'end';
  // GPS suggestion: one-shot lookup only when entering the trip (destination) form.
  if (type === 'trip' && !form.dataset.eventId) requestGpsSuggestion(form);
  if (!form.dataset.eventId) {
    const odo = form.elements.namedItem('odometer') as HTMLInputElement;
    const bat = form.elements.namedItem('batteryPct') as HTMLInputElement;
    if (type === 'start') {
      // Departure always opens with the previous day-end readings prefilled (re-filled only when blank).
      const pre = startPrefill();
      if (!odo.value) odo.value = pre.odo != null ? String(pre.odo) : '';
      if (!bat.value) bat.value = pre.bat != null ? String(pre.bat) : '';
    }
  }
  for (const name of ['batteryPct', 'batteryBeforePct', 'batteryAfterPct', 'chargeKwh', 'cost']) {
    const input = form.elements.namedItem(name) as HTMLInputElement; input.required = charge ? name !== 'batteryPct' : name === 'batteryPct';
  }
}

function formValue(form: HTMLFormElement): EventInput {
  const value = Object.fromEntries(new FormData(form));
  // Only use the "add new" job type input when its row is visible (user is in add-new mode);
  // otherwise a cancelled typed value would override the select.
  const jobNewVisible = !(form.querySelector('.job-new-row') as HTMLElement)?.hidden;
  const jobType = jobNewVisible ? value.jobTypeNew : null;
  return normalizeEvent({ type: value.type as EventType, occurredAt: new Date(`${value.occurredAt}:00+07:00`).toISOString(), odometer: Number(value.odometer), batteryPct: value.batteryPct === '' ? null : Number(value.batteryPct), batteryBeforePct: value.batteryBeforePct === '' ? null : Number(value.batteryBeforePct), batteryAfterPct: value.batteryAfterPct === '' ? null : Number(value.batteryAfterPct), chargeKwh: value.chargeKwh === '' ? null : Number(value.chargeKwh), cost: value.cost === '' ? null : Number(value.cost), locationName: String(value.locationName ?? ''), jobType: String(jobType || value.jobType || ''), notes: String(value.notes ?? '') });
}

function bindForm(form: HTMLFormElement) {
  setType((form.elements.namedItem('type') as HTMLInputElement).value as EventType, form);
  form.querySelectorAll<HTMLElement>('.day-seg button').forEach(b => b.addEventListener('click', () => { void switchFormType(b.dataset.day as EventType, form); }));
  const chargeLocation = form.elements.namedItem('locationName') as HTMLInputElement;
  const markChargeLocation = () => form.querySelectorAll<HTMLButtonElement>('[data-charge-location]').forEach(button => button.setAttribute('aria-pressed', String(chargeLocation.value.trim().toLowerCase() === button.dataset.chargeLocation!.toLowerCase())));
  form.querySelectorAll<HTMLButtonElement>('[data-charge-location]').forEach(button => button.addEventListener('click', () => {
    chargeLocation.value = button.dataset.chargeLocation!;
    chargeLocation.dispatchEvent(new Event('input', { bubbles: true }));
    markChargeLocation();
  }));
  chargeLocation.addEventListener('input', markChargeLocation);
  markChargeLocation();
  const jobSelect = form.elements.namedItem('jobType') as HTMLSelectElement | null;
  const jobNewRow = form.querySelector<HTMLElement>('.job-new-row');
  const jobNewInput = form.elements.namedItem('jobTypeNew') as HTMLInputElement | null;
  if (jobSelect && jobNewRow && jobNewInput) {
    const showJobSelect = () => { jobSelect.hidden = false; jobNewRow.hidden = true; };
    const showJobInput = () => { jobSelect.hidden = true; jobNewRow.hidden = false; jobNewInput.value = ''; jobNewInput.focus(); };
    const markJobButtons = () => form.querySelectorAll<HTMLButtonElement>('[data-job-type]').forEach(b => b.setAttribute('aria-pressed', String(jobSelect.value === b.dataset.jobType)));
    const pickJobType = (name: string) => {
      let option = [...jobSelect.options].find(o => o.value === name);
      if (!option) { option = document.createElement('option'); option.value = name; option.textContent = name; jobSelect.appendChild(option); }
      jobSelect.value = name;
      showJobSelect();
      jobSelect.dispatchEvent(new Event('input', { bubbles: true }));
      markJobButtons();
    };
    form.querySelector('.job-add')?.addEventListener('click', showJobInput);
    form.querySelector('.job-cancel')?.addEventListener('click', showJobSelect);
    form.querySelector('.job-confirm')?.addEventListener('click', () => { const name = jobNewInput.value.trim(); if (name) pickJobType(name); else showJobSelect(); });
    jobNewInput.addEventListener('keydown', e => {
      if (e.key === 'Enter') { e.preventDefault(); (form.querySelector('.job-confirm') as HTMLButtonElement).click(); }
      else if (e.key === 'Escape') showJobSelect();
    });
    form.querySelectorAll<HTMLButtonElement>('[data-job-type]').forEach(b => b.addEventListener('click', () => pickJobType(b.dataset.jobType!)));
    jobSelect.addEventListener('input', markJobButtons);
    markJobButtons();
  }
  const chip = form.querySelector<HTMLElement>('.time-chip'); const ti = form.elements.namedItem('occurredAt') as HTMLInputElement;
  chip?.addEventListener('click', () => { chip.hidden = true; ti.hidden = false; ti.focus(); });
  ti.addEventListener('blur', () => { ti.hidden = true; if (chip) { chip.hidden = false; chip.lastElementChild!.textContent = ti.value.replace('T', ' '); } });
  // Keep today's in-progress entry across app restarts; drafts from earlier days are dropped on restore.
  form.addEventListener('input', () => { form.dataset.editSequence = String(Number(form.dataset.editSequence ?? 0) + 1); const draft = Object.fromEntries(new FormData(form)) as Record<string, string>; draft.savedAt = new Date().toISOString(); if (!form.dataset.eventId) void saveDraft(draft, draft.type); });
  form.addEventListener('submit', async event => {
    event.preventDefault(); const error = form.querySelector<HTMLElement>('#form-error')!;
    const submitButton = form.querySelector<HTMLButtonElement>('button.primary');
    if (submitButton?.disabled) return;
    if (submitButton) submitButton.disabled = true;
    try {
      const input = formValue(form);
      if (!form.dataset.eventId) {
        const locked = dayState();
        if ((input.type === 'start' && locked.latest === 'start') || (input.type === 'end' && locked.latest === 'end')) { error.textContent = 'บันทึกสถานะนี้ของวันนี้แล้ว'; return; }
        if (blockedAfterReturn(input.type, locked.closed)) { error.textContent = 'กลับบ้านแล้ว กรุณาออกเดินทางใหม่ก่อนบันทึกจุดหมายหรือชาร์จ'; return; }
      }
      const eventId = form.dataset.eventId; const version = Number(form.dataset.version);
      const performSave = async () => {
        const pending = await enqueue(input, state?.revision ?? null, eventId ? { eventId, expectedVersion: version } : undefined);
        if (!eventId) await clearDraft(input.type);
        // Learn GPS coordinates for this location name (for future 300m suggestions).
        // Only for trip/charge with a location name and fresh GPS (within 10 min).
        if (!eventId && (input.type === 'trip' || input.type === 'charge') && input.locationName && lastGpsCoords && Date.now() - gpsLastLookup < GPS_CACHE_MS) {
          learnCoords(input.locationName, lastGpsCoords);
        }
        closeDialog();
        try { await flushAndReload(); } catch { /* The durable queue retains the saved item. */ }
        const remaining = await queue.get(pending.id);
        const message = saveFeedback(remaining?.state);
        if (!remaining && !eventId && (input.type === 'start' || input.type === 'end')) celebrate(input.type);
        else showSaveNotice(message);
      };
      if (!form.dataset.eventId && input.type === 'start' && dayState().closed) {
        // The day was already closed — warn before opening a new round.
        openDialog(`<div class="celebrate"><span class="celebrate-emoji">⚠️</span><p>กด "กลับบ้าน" ไปแล้ว<br>ต้องการออกเดินทางรอบใหม่ใช่หรือไม่?</p><button class="primary" id="round2-confirm">ออกเดินทางรอบใหม่</button><button class="danger" id="round2-cancel">ยกเลิก</button></div>`);
        document.querySelector('#round2-confirm')?.addEventListener('click', async () => { closeDialog(); try { await performSave(); } catch (cause) { error.textContent = cause instanceof Error ? cause.message : 'ข้อมูลไม่ถูกต้อง'; } });
        document.querySelector('#round2-cancel')?.addEventListener('click', closeDialog);
        return;
      }
      await performSave();
    } catch (cause) { error.textContent = cause instanceof Error ? cause.message : 'ข้อมูลไม่ถูกต้อง'; }
    finally { if (submitButton) submitButton.disabled = false; }
  });
}

function bindMain() {
  document.querySelectorAll<HTMLElement>('nav [data-tab]').forEach(button => button.addEventListener('click', () => {
    const t = button.dataset.tab!;
    void switchFormType((t === 'day' ? dayFormType(dayState()) : t) as EventType); window.scrollTo({ top: 0 });
  }));
  const form = document.querySelector<HTMLFormElement>('#event-form')!; bindForm(form);
  const syncNow = () => { refreshEventTime(document.querySelector<HTMLFormElement>('#event-form')); void flushAndReload(); };
  document.querySelector('#retry')?.addEventListener('click', syncNow);
  document.querySelector('#retry-panel')?.addEventListener('click', syncNow);
  bindSections();
  document.querySelectorAll<HTMLElement>('.retry-one').forEach(button => button.addEventListener('click', async () => { await retryMutation(button.dataset.id!, state?.revision ?? 'empty'); await flushAndReload(); }));
  document.querySelectorAll<HTMLElement>('.resolve-conflict').forEach(button => button.addEventListener('click', () => resolveConflict(button.dataset.id!)));
  void restoreFormDraft(form);
}


let formSwitch = 0;
// The event time always syncs to "now" when the app opens or the user presses
// sync; other draft fields are preserved. Never touches the edit dialog.
function refreshEventTime(form: HTMLFormElement | null) {
  if (!form || form.dataset.eventId) return;
  const input = form.elements.namedItem('occurredAt') as HTMLInputElement | null;
  if (!input || input.disabled) return;
  input.value = localValue();
  const stamp = form.querySelector('.time-chip span');
  if (stamp) stamp.textContent = input.value.replace('T', ' ');
}
async function restoreFormDraft(form: HTMLFormElement) {
  const type = (form.elements.namedItem('type') as HTMLInputElement).value;
  const sequence = form.dataset.editSequence;
  const draft = await loadDraft<Record<string,string>>(type).catch(() => null);
  if (form.dataset.editSequence !== sequence || !form.isConnected || (form.elements.namedItem('type') as HTMLInputElement).value !== type || !draft) return;
  if (!draft.savedAt || dayKey(draft.savedAt) !== dayKey(new Date().toISOString())) { await clearDraft(type); return; }
  for (const [name,value] of Object.entries(draft)) {
    if (name === 'savedAt' || name === 'type' || name === 'occurredAt' || (type === 'start' && (name === 'odometer' || name === 'batteryPct'))) continue;
    const input = form.elements.namedItem(name) as HTMLInputElement | HTMLSelectElement | null;
    if (input && !input.disabled) {
      // For selects, ensure a custom draft value has an option (from add-new).
      if (input.tagName === 'SELECT' && value && ![...(input as HTMLSelectElement).options].some(o => o.value === value)) {
        const option = document.createElement('option');
        option.value = value; option.textContent = value;
        (input as HTMLSelectElement).appendChild(option);
      }
      input.value = value;
    }
  }
  form.querySelectorAll<HTMLButtonElement>('[data-charge-location]').forEach(button => button.setAttribute('aria-pressed', String((form.elements.namedItem('locationName') as HTMLInputElement).value.trim().toLowerCase() === button.dataset.chargeLocation!.toLowerCase())));
  refreshEventTime(form);
}
async function switchFormType(type: EventType, form = document.querySelector<HTMLFormElement>('#event-form')!) {
  if (blockedAfterReturn(type, dayState().closed)) { showSaveNotice('กลับบ้านแล้ว กรุณาออกเดินทางใหม่ก่อน'); return; }
  const old = (form.elements.namedItem('type') as HTMLInputElement).value;
  if (old === type) { if (type === 'start') applyDepartureReadings(form); return; }
  const serial = ++formSwitch;
  await saveDraft({...Object.fromEntries(new FormData(form)), savedAt: new Date().toISOString()}, old);
  if (serial !== formSwitch || !form.isConnected) return;
  form.reset();
  for (const name of ['locationName','odometer','batteryPct','batteryBeforePct','batteryAfterPct','chargeKwh','cost','notes']) (form.elements.namedItem(name) as HTMLInputElement | HTMLSelectElement).value = '';
  (form.elements.namedItem('occurredAt') as HTMLInputElement).value = localValue();
  setType(type,form);
  const stamp = form.querySelector('.time-chip span');
  if (stamp) stamp.textContent = (form.elements.namedItem('occurredAt') as HTMLInputElement).value.replace('T',' ');
  await restoreFormDraft(form);
  if (type === 'start' && form.isConnected) applyDepartureReadings(form);
}
let noticeTimer: ReturnType<typeof setTimeout> | undefined;
function showSaveNotice(message: string) {
  document.querySelector('#save-notice')?.remove();
  if (noticeTimer) clearTimeout(noticeTimer);
  const notice = document.createElement('div'); notice.id = 'save-notice'; notice.className = 'save-notice'; notice.setAttribute('role','status'); notice.textContent = message;
  document.body.append(notice); noticeTimer = setTimeout(() => notice.remove(), 6000);
}

const dayKey = bangkokDayKey;
const num = (v: unknown) => Number(v ?? 0) || 0;
const fmt = (n: number, d = 1) => n.toLocaleString('th-TH', { maximumFractionDigits: d, useGrouping: false });
// Today's day-stage: the latest start/end bookend of the Bangkok day decides the locks
// (latest=start -> day is open; latest=end -> day is closed, a warned new round is allowed).
const dayState = () => {
  const byId = new Map<string, ApiEvent>();
  for (const event of [...allEvents, ...knownBookends, ...(state?.recent ?? [])]) {
    if (!byId.has(event.event_id) || event.version >= byId.get(event.event_id)!.version) byId.set(event.event_id, event);
  }
  return dayFlowState([
    ...[...byId.values()].map(e => ({type:e.type,occurredAt:e.occurred_at,sequence:Number(e.sequence ?? 0)})),
    ...pendingBookends.filter(item => item.state === 'queued' || item.state === 'syncing').map((item,index) => ({type:item.event.type,occurredAt:item.event.occurredAt,sequence:Number.MAX_SAFE_INTEGER - pendingBookends.length + index})),
  ]);
};

// Latest known readings for the "ล่าสุด" hint line — the truly latest event, whatever its type (ที่พัก/รับรถ/ส่งรถ/ลูกค้า).
const latestInfo = () => {
  const list = state?.recent ?? [];
  return {
    place: list[0]?.location_name_snapshot ?? '',
    odo: list.find(e => e.odometer != null)?.odometer as number | undefined,
    bat: (list.find(e => e.battery_pct != null)?.battery_pct ?? list.find(e => e.battery_after_pct != null)?.battery_after_pct) as number | undefined,
  };
};
// Departure prefill always comes from the previous day-end (จบวันครั้งก่อน) readings.
const startPrefill = () => lastReturnReadings([
  ...[...allEvents, ...knownBookends, ...(state?.recent ?? [])].map(e => ({eventId:e.event_id,version:e.version,type:e.type,occurredAt:e.occurred_at,sequence:Number(e.sequence ?? 0),odometer:e.odometer,batteryPct:e.battery_pct})),
  ...pendingBookends.filter(item => !item.eventId && (item.state === 'queued' || item.state === 'syncing')).map((item,index) => ({eventId:item.id,version:1,type:item.event.type,occurredAt:item.event.occurredAt,sequence:Number.MAX_SAFE_INTEGER - pendingBookends.length + index,odometer:item.event.odometer,batteryPct:item.event.batteryPct})),
]);
function applyDepartureReadings(form: HTMLFormElement) {
  if (form.dataset.eventId) return;
  const readings = startPrefill();
  (form.elements.namedItem('odometer') as HTMLInputElement).value = readings.odo != null ? String(readings.odo) : '';
  (form.elements.namedItem('batteryPct') as HTMLInputElement).value = readings.bat != null ? String(readings.bat) : '';
}

const latestHint = () => {
  const i = latestInfo();
  const parts = [i.place ? `ล่าสุด: ${escape(i.place)}` : '', i.odo != null ? `${fmt(i.odo)} km` : '', i.bat != null ? `🔋 ${i.bat}%` : ''].filter(Boolean);
  return parts.length ? `<small class="latest-hint">${parts.join(' · ')}</small>` : '';
};
const DAY = 864e5;
function periods() {
  const d = new Date(`${dayKey(new Date().toISOString())}T00:00:00Z`);
  const mon = d.getTime() - ((d.getUTCDay() + 6) % 7) * DAY; const iso = (t: number) => new Date(t).toISOString().slice(0, 10);
  return { month: iso(d.getTime()).slice(0, 7), week: Array.from({ length: 5 }, (_, i) => iso(mon + i * DAY)), from: iso(mon), to: iso(mon + 6 * DAY) };
}
function dayDistances() {
  const by = new Map<string, number[]>();
  for (const e of allEvents) { if (e.odometer == null) continue; const k = dayKey(e.occurred_at); by.set(k, [...(by.get(k) ?? []), num(e.odometer)]); }
  return new Map([...by].map(([k, v]) => [k, Math.max(...v) - Math.min(...v)] as [string, number]));
}
const sum = (list: ApiEvent[], key: 'charge_kwh' | 'cost') => list.reduce((s, e) => s + num(e[key]), 0);
const cstat = (label: string, list: ApiEvent[]) => `<div><small>${label}</small><strong><em>฿ </em>${fmt(sum(list, 'cost'), 2)}</strong><small>${fmt(sum(list, 'charge_kwh'))} kWh</small></div>`;
const tripRow = (e: ApiEvent) => `<button class="event-row" data-event-id="${escape(e.event_id)}"><span class="event-icon trip">${icon('pin')}</span><span class="event-copy"><strong>${escape(e.location_name_snapshot)}</strong><small>${escape(e.job_type_snapshot ?? '')} · ${thaiDate(e.occurred_at)}</small></span><span class="row-meta">${fmt(num(e.odometer))} km<br>🔋 ${e.battery_pct ?? '–'}%</span></button>`;
const chargeRow = (e: ApiEvent) => `<button class="event-row" data-event-id="${escape(e.event_id)}"><span class="event-icon charge">${icon('charge')}</span><span class="event-copy"><strong>${escape(e.location_name_snapshot)}</strong><small>${e.battery_before_pct ?? '–'}% → ${e.battery_after_pct ?? '–'}% · ${thaiDate(e.occurred_at)}</small></span><span class="row-meta"><strong>฿${fmt(num(e.cost), 2)}</strong><br>${fmt(num(e.charge_kwh))} kWh</span></button>`;

function sections() {
  const box = (empty: string) => stats === 'loading' ? '<div class="state"><span class="spin"></span>กำลังโหลดข้อมูล…</div>'
    : stats === 'error' ? '<div class="state err">โหลดข้อมูลไม่สำเร็จ<br><button class="ghost retry-stats">ลองอีกครั้ง</button></div>' : `<p class="state">${empty}</p>`;
  let week = box('ยังไม่มีข้อมูลระยะทาง'), month = box('ยังไม่มีข้อมูลเดือนนี้'), weekTotal = '';
  let trips = box('ยังไม่มีรายการถึงจุดหมายเดือนนี้'), chargeStats = box('ยังไม่มีข้อมูลการชาร์จ'), charges = box('ยังไม่มีการชาร์จเดือนนี้');
  if (stats === 'ready' && allEvents.length) {
    const p = periods(); const inMonth = (e: ApiEvent) => dayKey(e.occurred_at).startsWith(p.month);
    const inWeek = (e: ApiEvent) => { const k = dayKey(e.occurred_at); return k >= p.from && k <= p.to; };
    const newest = (a: ApiEvent, b: ApiEvent) => b.occurred_at.localeCompare(a.occurred_at);
    const dist = dayDistances(); const km = (k: string) => dist.get(k) ?? 0; const max = Math.max(1, ...p.week.map(km)); const names = ['จ', 'อ', 'พ', 'พฤ', 'ศ'];
    week = `<div class="bars">${p.week.map((k, i) => `<div><b>${fmt(km(k), 0)}</b><i style="height:${Math.max(4, km(k) / max * 100)}%"></i><span>${names[i]}</span></div>`).join('')}</div>`;
    weekTotal = `รวม ${fmt(p.week.reduce((s, k) => s + km(k), 0))} km`;
    const keys = [...dist.keys()].filter(k => k.startsWith(p.month)); const total = keys.reduce((s, k) => s + km(k), 0);
    month = `<div class="stat-grid three"><div><small>ระยะรวม</small><strong>${fmt(total)}<em> km</em></strong></div><div><small>วันทำงาน</small><strong>${keys.length}</strong></div><div><small>เฉลี่ย/วัน</small><strong>${fmt(keys.length ? total / keys.length : 0)}</strong></div></div><p class="hint">นับวันที่ 1 ถึงสิ้นเดือน · รีเซทอัตโนมัติเมื่อขึ้นเดือนใหม่</p>`;
    const t = allEvents.filter(e => e.type === 'trip' && inMonth(e)).sort(newest); if (t.length) trips = t.map(tripRow).join('');
    const c = allEvents.filter(e => e.type === 'charge'); const cm = c.filter(inMonth).sort(newest);
    chargeStats = `<div class="stat-grid three">${cstat('สัปดาห์นี้', c.filter(inWeek))}${cstat('เดือนนี้', cm)}${cstat('ทั้งหมด', c)}</div>`;
    if (cm.length) charges = cm.map(chargeRow).join('');
  }
  const hide = (t: string) => tab === t ? '' : ' hidden';
  return `<div class="tab-sec" data-tab="day"${hide('day')}><article class="panel"><div class="section-title"><h2>ระยะทางสัปดาห์นี้ (จ–ศ)</h2><small>${weekTotal}</small></div>${week}</article><article class="panel"><div class="section-title"><h2>สรุปวันทำงานเดือนนี้</h2></div>${month}</article></div>`
    + `<div class="tab-sec" data-tab="trip"${hide('trip')}><article class="panel"><div class="section-title"><h2>ถึงจุดหมายเดือนนี้</h2><button class="link-button open-history">ดูทั้งหมด ›</button></div><div class="timeline">${trips}</div></article></div>`
    + `<div class="tab-sec" data-tab="charge"${hide('charge')}><article class="panel"><div class="section-title"><h2>สรุปการชาร์จ</h2></div>${chargeStats}</article><article class="panel"><div class="section-title"><h2>การชาร์จเดือนนี้</h2><button class="link-button open-history">ดูทั้งหมด ›</button></div><div class="timeline">${charges}</div></article></div>`;
}
function bindSections() {
  document.querySelectorAll<HTMLElement>('#sections-host .event-row').forEach(b => b.addEventListener('click', () => openEvent(b.dataset.eventId!)));
  document.querySelectorAll('#sections-host .retry-stats').forEach(b => b.addEventListener('click', () => void refreshStats()));
  document.querySelectorAll('#sections-host .open-history').forEach(b => b.addEventListener('click', () => openHistory()));
}
function paintSections() { const host = document.querySelector('#sections-host'); if (host) { host.innerHTML = sections(); bindSections(); } }
// Continuation loader for search: appends older pages onto the stats cache until
// history is exhausted. refreshStats() below always reseeds the cache from scratch.
function loadAllEvents(limit = 50, maxPages = 200) {
  if (allEventsComplete) return Promise.resolve();
  eventsLoad ??= (async () => {
    for (let page = 0; page < maxPages && !allEventsComplete; page++) {
      const r = await history(null, allEventsCursor, limit);
      const events = r.events as ApiEvent[];
      allEvents.push(...events); allEventsCursor = r.nextCursor;
      if (events.length < limit || !r.nextCursor) allEventsComplete = true;
    }
  })().finally(() => { eventsLoad = null; });
  return eventsLoad;
}
async function refreshStats() {
  stats = allEvents.length ? 'ready' : 'loading'; paintSections();
  try {
    const out: ApiEvent[] = []; let cursor: string | null = null; let complete = false;
    for (let i = 0; i < 40; i++) {
      const r = await history(null, cursor); const events = r.events as ApiEvent[];
      out.push(...events);
      if (events.length < 50 || !r.nextCursor) { complete = true; break; }
      cursor = r.nextCursor;
    }
    allEvents = out; allEventsComplete = complete; allEventsCursor = complete ? null : cursor; stats = 'ready';
  } catch { stats = allEvents.length ? 'ready' : 'error'; }
  paintSections();
  // The full history may reveal that today's start/end already exists — refresh the day-seg locks.
  const form = document.querySelector<HTMLFormElement>('#event-form');
  if (form && !form.dataset.eventId) {
    const type = (form.elements.namedItem('type') as HTMLInputElement).value as EventType;
    if ((type === 'start' || type === 'end') && type !== dayFormType(dayState())) void switchFormType(dayFormType(dayState()), form);
    else { setType(type, form); if (type === 'start' && !form.dataset.editSequence) applyDepartureReadings(form); }
  }
}

function openDialog(html: string) { const dialog = document.querySelector<HTMLDialogElement>('#dialog')!; document.querySelector('#dialog-body')!.innerHTML = html; if (!dialog.open) dialog.showModal(); }
function closeDialog() { document.querySelector<HTMLDialogElement>('#dialog')?.close(); }

// A friendly one-tap popup after every successful save.
const celebrations: Record<EventType, string[]> = {
  start: ['🚗 ออกเดินทางปลอดภัยนะครับ ขอให้ทุกเส้นทางราบรื่น ถึงจุดหมายทุกที่', '🌤 วันนี้ขอให้เดินทางสบายๆ งานราบรื่น และกลับบ้านปลอดภัยครับ'],
  trip: ['💪 เก่งมาก! ถึงอีกจุดหมายแล้ว วันนี้ขยันอีกงานเรียบร้อยครับ', '🎯 ถึงจุดหมายปลอดภัยแล้ว ต่อไปสู้ๆ อีกแรงเดียวเชียว!'],
  charge: ['⚡ ชาร์จพลังงานเรียบร้อย รถพร้อมลุย คุณก็มีพลังต่อได้เต็มที่', '🔋 แบตเต็มแล้ว พร้อมเดินทางต่ออย่างมั่นใจครับ'],
  end: ['🏠 กลับถึงที่พักแล้ว ขับขี่ปลอดภัยมาทั้งวัน พักผ่อนให้เต็มที่นะครับ', '❤️ วันนี้เหนื่อยน้อยลงเพราะบันทึกครบ ถึงบ้านแล้วพักให้สบายนะครับ'],
};
function celebrate(type: EventType) {
  const list = celebrations[type]; const text = list[Math.floor(Math.random() * list.length)];
  const emoji = { start: '🚗', trip: '🎯', charge: '⚡', end: '🏠' }[type];
  openDialog(`<div class="celebrate"><span class="celebrate-emoji">${emoji}</span><p>${escape(text)}</p><button class="primary" id="celebrate-ok">ขอบคุณครับ 🙏</button></div>`);
  document.querySelector('#celebrate-ok')?.addEventListener('click', closeDialog);
}

async function openEvent(id: string) {
  try { const event = await latestEvent(id) as ApiEvent; openDialog(`<h2>แก้ไขรายการ</h2><p class="muted dialog-subtitle">${thaiDate(event.occurred_at)}</p>${eventForm(event)}`); bindForm(document.querySelector<HTMLFormElement>('#dialog #event-form')!); }
  catch (error) { openDialog(`<p class="error">${escape(error instanceof Error ? error.message : 'โหลดรายการไม่สำเร็จ')}</p>`); }
}

const searchable = (e: ApiEvent) => ({ eventId: e.event_id, occurredAt: e.occurred_at, locationName: e.location_name_snapshot, jobType: e.job_type_snapshot, notes: e.notes, odometer: e.odometer, raw: e });
// allEvents is newest-first; keep the first (latest) revision per event.
const uniqueEvents = () => { const byId = new Map<string, ApiEvent>(); for (const e of allEvents) { if (!byId.has(e.event_id)) byId.set(e.event_id, e); } return [...byId.values()]; };
const localHistoryHTML = () => uniqueEvents().map(e => eventCard(e)).join('');

function searchCard(e: ApiEvent, field: SearchField) {
  const ic = e.type === 'charge' ? 'charge' : e.type === 'trip' ? 'pin' : e.type === 'start' ? 'car' : 'home';
  const hint = field === 'odometer' ? `เลขไมล์: ${fmt(e.odometer)} km` : field === 'date' ? `เลขไมล์: ${e.odometer ?? '–'} km` : field === 'jobType' ? `ประเภทงาน: ${e.job_type_snapshot ?? ''}` : field === 'notes' ? `หมายเหตุ: ${(e.notes ?? '').slice(0, 80)}` : '';
  return `<button class="event-row" data-event-id="${escape(e.event_id)}"><span class="event-icon ${e.type}">${icon(ic)}</span><span class="event-copy"><strong>${escape(e.location_name_snapshot)}</strong><small>${typeLabels[e.type] ?? escape(e.type)} · ${thaiDate(e.occurred_at)}</small>${hint ? `<small class="match-hint">${escape(hint)}</small>` : ''}</span><span class="chevron">›</span></button>`;
}

function dayGroupHTML(day: string, events: ApiEvent[], matched: Set<string>) {
  const label = new Date(`${day}T00:00:00+07:00`).toLocaleDateString('th-TH', { timeZone: 'Asia/Bangkok', weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  return `<section class="day-group"><h3>${label} · ${events.length} รายการ</h3>${events.map((e, i) => {
    const ic = e.type === 'charge' ? 'charge' : e.type === 'trip' ? 'pin' : e.type === 'start' ? 'car' : 'home';
    return `<button class="event-row${matched.has(e.event_id) ? ' is-match' : ''}" data-event-id="${escape(e.event_id)}"><span class="seq">${i + 1}</span><span class="event-icon ${e.type}">${icon(ic)}</span><span class="event-copy"><strong>${escape(e.location_name_snapshot)}</strong><small>${typeLabels[e.type] ?? escape(e.type)} · ${thaiTime(e.occurred_at)}</small></span></button>`;
  }).join('')}</section>`;
}

function historyShell(listHTML: string, moreHTML: string) {
  const frequent = topLocations(uniqueEvents().map(searchable), 6);
  return `<div class="section-title"><h2>ประวัติทั้งหมด</h2></div><div class="history-search"><input id="history-search" type="search" list="history-locations" autocomplete="off" enterkeyhint="search" aria-label="ค้นหาประวัติด้วยสถานที่ เลขไมล์ หรือวันที่" aria-describedby="history-search-help" placeholder="สถานที่ เลขไมล์ หรือวัน/เดือน/ปี" value="${escape(historyQuery)}"><datalist id="history-locations">${frequent.map(l => `<option value="${escape(l.name)}">`).join('')}</datalist><small id="history-search-help" class="search-help">ลอง 19000 · 3/10/2569 · ตุลาคม 2569<br>ค้นร่วมกันได้ เช่น Spark 3/10/2569 หรือช่วงไมล์ 19000-20000</small></div><div class="segmented history-mode"><button type="button" data-mode="matches"${historyMode === 'matches' ? ' class="active"' : ''}>รายการที่ค้นเจอ</button><button type="button" data-mode="day"${historyMode === 'day' ? ' class="active"' : ''}>ลำดับทั้งวัน</button></div>${historyQuery.trim() || !frequent.length ? '' : `<div class="chip-row">${frequent.map(l => `<button type="button" class="search-chip" data-location="${escape(l.name)}">${escape(l.name)}</button>`).join('')}</div>`}<div id="history-list" class="timeline">${listHTML}</div>${moreHTML}`;
}

function bindHistoryRows() { document.querySelectorAll<HTMLElement>('#dialog #history-list .event-row').forEach(button => button.addEventListener('click', () => openEvent(button.dataset.eventId!))); }

function bindSearchChips() {
  const input = document.querySelector<HTMLInputElement>('#history-search');
  document.querySelectorAll<HTMLElement>('#dialog .search-chip').forEach(button => button.addEventListener('click', () => {
    historyQuery = button.dataset.location ?? ''; if (input) input.value = historyQuery;
    syncHistoryChrome(); void renderHistoryResults();
  }));
}

function bindSearchControls() {
  const input = document.querySelector<HTMLInputElement>('#history-search')!;
  input.addEventListener('input', () => { historyQuery = input.value; syncHistoryChrome(); void renderHistoryResults(); });
  document.querySelectorAll<HTMLElement>('#dialog .history-mode button').forEach(button => button.addEventListener('click', () => {
    historyMode = button.dataset.mode as 'matches' | 'day';
    document.querySelectorAll('#dialog .history-mode button').forEach(b => b.classList.toggle('active', b === button));
    void renderHistoryResults();
  }));
  bindSearchChips();
}

function syncHistoryChrome() { document.querySelectorAll<HTMLElement>('#dialog .chip-row').forEach(chips => { chips.hidden = historyQuery.trim() !== ''; }); }

function syncHistorySuggestions() {
  const frequent = topLocations(uniqueEvents().map(searchable), 6);
  const datalist = document.querySelector('#history-locations'); if (datalist) datalist.innerHTML = frequent.map(l => `<option value="${escape(l.name)}">`).join('');
  const chips = document.querySelector<HTMLElement>('#dialog .chip-row');
  if (chips && historyQuery.trim() === '' && frequent.length) { chips.innerHTML = frequent.map(l => `<button type="button" class="search-chip" data-location="${escape(l.name)}">${escape(l.name)}</button>`).join(''); bindSearchChips(); }
}

async function renderHistoryResults() {
  const list = document.querySelector<HTMLElement>('#history-list'); if (!list) return;
  const searching = historyQuery.trim() !== '' || historyMode === 'day';
  document.querySelector('#history-more')?.toggleAttribute('hidden', searching || allEventsComplete);
  if (!searching) {
    if (list.dataset.mode === 'search') { list.innerHTML = historyBase; list.removeAttribute('data-mode'); bindHistoryRows(); return; }
    if (allEventsComplete && !historyLocal) { list.innerHTML = localHistoryHTML(); bindHistoryRows(); historyLocal = true; }
    return;
  }
  if (list.dataset.mode !== 'search') { historyBase = list.innerHTML; list.dataset.mode = 'search'; }
  if (!allEventsComplete) {
    list.innerHTML = '<div class="state"><span class="spin"></span>กำลังโหลดประวัติทั้งหมด…</div>';
    try { await loadAllEvents(100, 200); } catch { list.innerHTML = '<p class="error">โหลดประวัติไม่สำเร็จ</p>'; return; }
    if (!document.querySelector('#history-list') || (historyQuery.trim() === '' && historyMode === 'matches')) return;
  }
  const mapped = uniqueEvents().map(searchable);
  const matches = searchHistoryEvents(mapped, historyQuery);
  if (historyMode === 'day') {
    const matchedIds = new Set(matches.map(m => m.event.eventId));
    const matchedDays = new Set(matches.map(m => dayKey(m.event.occurredAt)));
    const groups = groupEventsByDay(mapped.filter(m => matchedDays.has(dayKey(m.occurredAt))));
    list.innerHTML = groups.map(g => dayGroupHTML(g.day, g.events.map(x => x.raw), matchedIds)).join('') || '<p class="muted">ไม่พบรายการที่ค้นหา</p>';
  } else {
    list.innerHTML = matches.map(m => searchCard(m.event.raw, m.field)).join('') || '<p class="muted">ไม่พบรายการที่ค้นหา</p>';
  }
  bindHistoryRows();
}

async function openHistory(cursor: string | null = null) {
  try {
    if (!cursor) { historyQuery = ''; historyMode = 'matches'; historyLocal = false; }
    if (!cursor && allEventsComplete) {
      openDialog(historyShell(localHistoryHTML() || '<p class="muted">ยังไม่มีรายการ</p>', ''));
      bindHistoryRows(); bindSearchControls(); historyLocal = true;
      return;
    }
    const result = await history(null, cursor);
    const previous = cursor ? document.querySelector('#history-list')?.innerHTML ?? '' : '';
    const list = previous + result.events.map(e => eventCard(e as ApiEvent)).join('');
    openDialog(historyShell(list || '<p class="muted">ยังไม่มีรายการ</p>', result.events.length === 50 && result.nextCursor ? '<button id="history-more" class="secondary">โหลดเพิ่มเติม</button>' : ''));
    bindHistoryRows();
    document.querySelector('#history-more')?.addEventListener('click', () => openHistory(result.nextCursor));
    bindSearchControls();
    void loadAllEvents(100, 200).then(() => { syncHistorySuggestions(); void renderHistoryResults(); }).catch(() => {});
  } catch (error) { openDialog(`<p class="error">${escape(error instanceof Error ? error.message : 'โหลดประวัติไม่สำเร็จ')}</p>`); }
}

// The menu's charge dashboard opens as a dialog on the all-time totals (the
// charge tab keeps its own copy with the newest-charge-month default); the two
// share chargeMonth so a month picked in either place carries over.
async function openChargeDashboard() {
  const renderDash = () => {
    openDialog(stats === 'loading' && !allEvents.length
      ? '<article class="panel"><div class="section-title"><h2>แดชบอร์ดค่าชาร์จ</h2></div><div class="state dash-loading"><span class="spin"></span>กำลังโหลดข้อมูล…</div></article>'
      : chargeDashboardHTML(allEvents, chargeMonth ?? 'all'));
    document.querySelectorAll<HTMLElement>('#dialog [data-charge-month]').forEach(b => b.addEventListener('click', () => { chargeMonth = b.dataset.chargeMonth!; renderDash(); }));
    document.querySelector('#dialog .open-history')?.addEventListener('click', () => { closeDialog(); openHistory(); });
  };
  renderDash();
  if (stats !== 'ready') { try { await refreshStats(); } catch { /* keep the empty state */ } if (document.querySelector('#dialog .dash-loading')) renderDash(); }
}

async function openSettings() {
  try {
    settings ??= await getSettings();
    openDialog(`<h2>การเชื่อมต่อ</h2><p class="muted dialog-subtitle">การเปลี่ยนแปลงมีผลกับรายการที่บันทึกหลังจากนี้</p><form id="settings-form" class="settings-form"><label><input type="checkbox" name="line"${settings.line_enabled ? ' checked' : ''}> แจ้งเตือนผ่าน LINE</label><label><input type="checkbox" name="sheets"${settings.sheets_enabled ? ' checked' : ''}> ส่งสำเนาไป Google Sheets</label><label><input type="checkbox" name="calendar"${settings.calendar_enabled ? ' checked' : ''}> สร้างรายการใน Google Calendar</label><p id="settings-error" class="error" role="alert"></p><button class="primary" type="submit">บันทึกการตั้งค่า</button></form>`);
    document.querySelector<HTMLFormElement>('#settings-form')?.addEventListener('submit', async event => {
      event.preventDefault(); const form = event.currentTarget as HTMLFormElement; const error = form.querySelector<HTMLElement>('#settings-error')!;
      try { settings = await updateSettings({ line_enabled:(form.elements.namedItem('line') as HTMLInputElement).checked, sheets_enabled:(form.elements.namedItem('sheets') as HTMLInputElement).checked, calendar_enabled:(form.elements.namedItem('calendar') as HTMLInputElement).checked }); closeDialog(); }
      catch (cause) { error.textContent = cause instanceof Error ? cause.message : 'บันทึกการตั้งค่าไม่สำเร็จ'; }
    });
  } catch (cause) { openDialog(`<p class="error">${escape(cause instanceof Error ? cause.message : 'โหลดการตั้งค่าไม่สำเร็จ')}</p>`); }
}

async function resolveConflict(id: string) {
  const item = await queue.get(id); if (!item) return;
  if (!item.eventId) {
    openDialog(`<h2>ข้อมูลใหม่กว่าบนอุปกรณ์อื่น</h2><p>รายการนี้ยังไม่เคยบันทึก คุณสามารถส่งใหม่ต่อจากข้อมูลล่าสุดได้อย่างปลอดภัย</p><button id="rebase-new" class="primary">ส่งรายการนี้ใหม่</button><button id="discard" class="danger">ยกเลิกรายการนี้</button>`);
    document.querySelector('#rebase-new')?.addEventListener('click', async () => { await retryMutation(id, state?.revision ?? 'empty'); closeDialog(); await flushAndReload(); });
  } else {
    const server = await latestEvent(item.eventId) as ApiEvent;
    openDialog(`<h2>รายการถูกแก้ไขจากที่อื่น</h2><div class="compare"><div><small>ข้อมูลในเครื่อง</small><strong>${escape(item.event.locationName)}</strong><span>${escape(item.event.odometer)} km</span></div><div><small>ข้อมูลล่าสุด</small><strong>${escape(server.location_name_snapshot)}</strong><span>${escape(server.odometer)} km</span></div></div><p class="muted">เลือก “ใช้ข้อมูลในเครื่อง” เพื่อสร้าง revision ใหม่ทับข้อมูลล่าสุด หรือยกเลิกคิวเพื่อเก็บข้อมูลบนเซิร์ฟเวอร์</p><button id="overwrite" class="primary">ใช้ข้อมูลในเครื่อง</button><button id="discard" class="danger">ใช้ข้อมูลล่าสุด</button>`);
    document.querySelector('#overwrite')?.addEventListener('click', async () => { await retryMutation(id, state?.revision ?? 'empty', server.version); closeDialog(); await flushAndReload(); });
  }
  document.querySelector('#discard')?.addEventListener('click', async () => { await queue.remove(id); closeDialog(); await flushAndReload(); });
}

function updateNetwork() { const element = document.querySelector('#network'); if (element) element.textContent = navigator.onLine ? 'เชื่อมต่อแล้ว' : 'ไม่มีอินเทอร์เน็ต รายการใหม่จะรอซิงก์'; }
async function flushAndReload() {
  await flushQueue(render);
  if (navigator.onLine) {
    const results = await Promise.allSettled([bootstrap(), latestBookend('start'), latestBookend('end')]);
    if (results[0].status === 'fulfilled') state = results[0].value as Boot;
    for (let index = 1; index < results.length; index++) {
      const result = results[index];
      if (result.status === 'fulfilled') {
        const type = index === 1 ? 'start' : 'end';
        knownBookends = knownBookends.filter(event => event.type !== type);
        if (result.value) knownBookends.push(result.value as ApiEvent);
      }
    }
  }
  await render(); void refreshStats();
}
async function start() {
  if (!configured) { root.innerHTML = `<section class="setup"><img src="/worktripev-logo.png" alt="โลโก้ WorkTripEV"><h1>WorkTripEV</h1><p>ยังไม่ได้ตั้งค่า Supabase</p><code>VITE_SUPABASE_URL<br>VITE_SUPABASE_PUBLISHABLE_KEY</code></section>`; return; }
  const session = await recoverSession(supabase.auth);
  if (!session) { root.innerHTML = `<section class="login"><img src="/worktripev-logo.png" alt="โลโก้ WorkTripEV"><small>WORKTRIPEV</small><h1>ทุกการเดินทาง<br>เก็บครบในที่เดียว</h1><p>บันทึกเลขไมล์ แบตเตอรี่ การชาร์จ และค่าใช้จ่าย แม้สัญญาณขาดหาย</p><button id="login" class="primary">เข้าสู่ระบบด้วย Google</button><small class="menu-version">${versionLabel()}</small></section>`; document.querySelector('#login')?.addEventListener('click', () => signIn()); return; }
  await flushAndReload();
}

window.addEventListener('online', flushAndReload); window.addEventListener('offline', updateNetwork);
// Supabase holds an auth lock while notifying subscribers. Defer API work until
// the notification returns or calls such as getSession()/rpc() can deadlock.
supabase.auth.onAuthStateChange(() => { setTimeout(() => { void start(); }, 0); });
void start();
